/* eslint-disable @typescript-eslint/no-non-null-assertion */
/* eslint-disable react/no-unstable-nested-components */

import {
  Box,
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  ButtonGroup,
  Circle,
  Flex,
  HStack,
  Icon,
  Link,
  Stack,
  Text,
} from '@chakra-ui/react';
import {
  CircleCaret,
  DirectAssignmentInstance,
  SingleDocument,
  Wifi,
} from '@/client/components/icons/ContinuIcons';
import {
  Navigate,
  Link as RouterLink,
  useLocation,
  useNavigate,
  useParams,
  useSearchParams,
} from 'react-router-dom';
import { useEffect, useMemo, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';

import AdminAssignmentsService from '../api/AdminAssignmentsService';
import AssignmentBulkActionsButtonGroup from '../buttons/AssignmentBulkActionsButtonGroup';
import AssignmentDetailsActionGroup from '../AssignmentDetailsActionGroup';
import AssignmentDetailsInstancesView from '../assignment-details/AssignmentDetailsInstancesView';
import AssignmentDetailsRecipientsView from '../assignment-details/AssignmentDetailsRecipientsView';
import AssignmentDetailsStatGroup from '../AssignmentDetailsStatGroup';
import AssignmentDetailsView from '../assignment-details/AssignmentDetailsView';
import AssignmentTabButton from '../buttons/AssignmentTabButton';
import ErrorAlert from '@/client/components/data-display/ErrorAlert';
import Loading from '@/client/components/media/Loading';
import NewInstancesBanner from '../overlay/NewInstancesBanner';
import WorkflowSearch from '../input/WorkflowSearch';
import { WorkflowType } from '@/client/services/api/graphql/gql/graphql';
import { format } from 'date-fns';
import { useAccessHelper } from '@/client/services/hooks/auth/useAccessHelper';
import { useAdminAssignmentDetailsStore } from '../state/adminAssignmentDetailsStore';
import { useCheckAccessProfile } from '../hooks/useCheckAccessProfile';
import { useTranslation } from 'react-i18next';

function AssignmentTypeLabel({ type }: { type: 'direct' | 'automated' | 'instance' }) {
  useCheckAccessProfile();
  const { t } = useTranslation();

  const typeMap = {
    direct: {
      label: t('admin.assignments.details.direct'),
      textColor: 'warmNeutral.900',
      backgroundColor: 'warmNeutral.100',
      iconContainerColor: 'warmNeutral.200',
      icon: SingleDocument,
    },
    automated: {
      label: t('admin.assignments.details.automated'),
      textColor: '#2CBE74',
      backgroundColor: '#DDF8E5',
      iconContainerColor: '#C9EFD4',
      icon: Wifi,
    },
    instance: {
      label: t('admin.assignments.details.direct.instance'),
      textColor: '#32B9A0',
      backgroundColor: '#C5FFF4',
      iconContainerColor: '#8FFFEA',
      icon: DirectAssignmentInstance,
    },
  };

  return (
    <HStack
      spacing={1}
      padding={3}
      borderRadius="4px"
      backgroundColor={typeMap[type].backgroundColor}
    >
      <Circle size={6} backgroundColor={typeMap[type].iconContainerColor}>
        <Icon as={typeMap[type].icon} color={typeMap[type].textColor} />
      </Circle>

      <Text fontSize="14px" fontWeight={600} color={typeMap[type].textColor}>
        {typeMap[type].label}
      </Text>
    </HStack>
  );
}

function CustomBreadcrumbItem({
  to,
  label,
  isCurrentPage = false,
}: {
  to: string;
  label: string;
  isCurrentPage?: boolean;
}) {
  return (
    <BreadcrumbItem isCurrentPage={isCurrentPage} marginRight={2}>
      <BreadcrumbLink
        as={RouterLink}
        to={to}
        color={isCurrentPage ? 'brand.primary' : 'neutral.500'}
        _hover={{ cursor: isCurrentPage ? 'default' : 'pointer', color: 'brand.primary' }}
      >
        <HStack>
          <CircleCaret />

          <Text fontSize="16px" fontWeight={600}>
            {label}
          </Text>
        </HStack>
      </BreadcrumbLink>
    </BreadcrumbItem>
  );
}

export default function AssignmentDetails() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { id, instanceId } = useParams();
  const location = useLocation();
  const { pathname } = location;
  const [searchParams, setSearchParams] = useSearchParams();
  const { recipientSelection, setRecipientSelection } = useAdminAssignmentDetailsStore();
  const { allowance, isAdmin } = useAccessHelper();
  const [showInstancesBanner, setShowInstancesBanner] = useState(true);

  const { assignments: assignmentsAllowed, workflows: workflowsAllowed } = allowance;

  const queryClient = useQueryClient();

  const isInstanceDetails = pathname.includes('instance');
  const type = searchParams.get('type');

  const { data, isLoading, isError } = useQuery({
    enabled: !!id,
    queryKey: ['getWorkflowDetailsViewData', id],
    queryFn: async () => {
      const [workflowData, workflowStatsData] = await Promise.all([
        AdminAssignmentsService.getWorkflowDetails(id),
        AdminAssignmentsService.getWorkflowStats(id),
      ]);

      return { workflowData, workflowStatsData };
    },
  });

  const {
    data: instanceData,
    isLoading: instanceDataIsLoading,
    isError: instanceDataIsError,
  } = useQuery({
    enabled: !!instanceId,
    queryKey: ['getWorkflowDetailsViewInstanceData', instanceId],
    queryFn: async () => AdminAssignmentsService.getWorkflowResultGroupDetails(instanceId),
  });

  const assignmentActionPayload = useMemo(
    () => ({
      assignmentIds: Object.keys(recipientSelection),
    }),
    [recipientSelection],
  );

  const tabButtonOptions = useMemo(
    () => [
      {
        label: t('admin.assignments.details.assignmentInstances'),
        status: type === 'instances' ? 'active' : 'inactive',
        enabled:
          !isInstanceDetails && data?.workflowData?.settings.type === WorkflowType.PointInTime,
        onClick: () => setSearchParams({ type: 'instances' }),
      },
      {
        label: isInstanceDetails
          ? t('admin.assignments.details.instanceRecipients')
          : t('admin.assignments.details.assignmentRecipients'),
        status: type === 'recipients' ? 'active' : 'inactive',
        enabled: true,
        onClick: () => setSearchParams({ type: 'recipients' }),
      },
      {
        label: isInstanceDetails
          ? t('admin.assignments.details.instanceDetails')
          : t('admin.assignments.details.assignmentDetails'),
        status: type === 'details' ? 'active' : 'inactive',
        enabled:
          isInstanceDetails || data?.workflowData?.settings.type !== WorkflowType.PointInTime,
        onClick: () => setSearchParams({ type: 'details' }),
      },
    ],
    [data?.workflowData, isInstanceDetails, type],
  );

  useEffect(() => {
    if (data?.workflowData.settings.type !== WorkflowType.PointInTime && !workflowsAllowed) {
      return navigate('/admin/assignments/overview');
    }

    if (
      !assignmentsAllowed &&
      (data?.workflowData.settings.type === WorkflowType.PointInTime || isInstanceDetails)
    ) {
      return navigate('/admin/assignments/overview');
    }

    if (data?.workflowData?.settings.type !== WorkflowType.PointInTime || isInstanceDetails) {
      setSearchParams({ type: 'recipients' });

      return;
    }

    setSearchParams({ type: 'instances' });
  }, [data?.workflowData, isInstanceDetails]);

  if (!isAdmin()) return <Navigate to="/explore" replace />;

  if (isLoading || (isInstanceDetails && instanceDataIsLoading)) return <Loading />;

  if (isError || (isInstanceDetails && instanceDataIsError))
    return (
      <ErrorAlert
        title={t('admin.assignments.details.loadingError')}
        backAction={{
          to: '/admin/assignments/overview',
          label: t('admin.assignments.details.backToOverview'),
          isAngular: false,
        }}
      />
    );

  return (
    <Box marginTop={-4}>
      <Stack marginX={8} marginY={2}>
        {showInstancesBanner &&
          data.workflowData.settings.type === WorkflowType.PointInTime &&
          !isInstanceDetails && <NewInstancesBanner onHide={() => setShowInstancesBanner(false)} />}

        <Breadcrumb marginTop={8} marginBottom={12}>
          <CustomBreadcrumbItem
            to={`/admin/assignments/overview?type=${
              data.workflowData.settings.type === WorkflowType.PointInTime ? 'direct' : 'automated'
            }`}
            label={
              data.workflowData.settings.type === WorkflowType.PointInTime
                ? t('admin.assignments.details.directAssignments')
                : t('admin.assignments.details.automatedAssignments')
            }
          />

          <CustomBreadcrumbItem
            to={`/admin/assignments/details/${data.workflowData.id}?type=${
              data.workflowData.settings.type === WorkflowType.PointInTime
                ? 'instances'
                : 'recipients'
            }`}
            label={data.workflowData.settings.name}
            isCurrentPage={!isInstanceDetails}
          />

          {isInstanceDetails && (
            <CustomBreadcrumbItem
              to={`/admin/assignments/details/${data.workflowData.id}/instance/${instanceId}?type=recipients`}
              label={format(new Date(instanceData?.createdAt), 'MMMM dd, yyyy h:mm a')}
              isCurrentPage
            />
          )}
        </Breadcrumb>

        <Stack marginBottom={8}>
          <Flex justifyContent="space-between" alignItems="center">
            <AssignmentTypeLabel
              type={
                isInstanceDetails
                  ? 'instance'
                  : data.workflowData.settings.type === WorkflowType.PointInTime
                  ? 'direct'
                  : 'automated'
              }
            />

            <AssignmentDetailsActionGroup
              data={isInstanceDetails ? instanceData! : data.workflowData}
              isComplete={
                isInstanceDetails
                  ? instanceData?.completedAssignments === instanceData?.totalAssignments
                  : data.workflowData.percentComplete === 100
              }
              isWorkflowResultGroup={isInstanceDetails}
            />
          </Flex>

          <Text fontSize="28px" fontWeight={600}>
            {isInstanceDetails
              ? format(new Date(instanceData?.createdAt), 'MMMM dd, yyyy h:mm a')
              : data.workflowData.settings.name}
          </Text>

          {isInstanceDetails && (
            <Text fontSize="16px" fontWeight={400} color="neutral.700">
              {t('admin.assignments.details.instanceOf')}
              <Link
                as={RouterLink}
                to={`/admin/assignments/details/${data.workflowData.id}`}
                textDecoration="underline"
                fontWeight={500}
              >
                {data.workflowData.settings.name}
              </Link>
            </Text>
          )}

          {!isInstanceDetails && (
            <Text fontSize="16px" fontWeight={500} color="baseBlack">
              {t('admin.assignments.details.createdOn')}{' '}
              {format(new Date(data.workflowData.createdAt), 'MMMM dd, yyyy')}
            </Text>
          )}
        </Stack>

        {!isInstanceDetails && (
          <AssignmentDetailsStatGroup
            workflowData={data.workflowData}
            workflowStatsData={data.workflowStatsData}
          />
        )}
      </Stack>

      <Flex
        justifyContent="space-between"
        alignItems="center"
        backgroundColor="neutral.0"
        borderY="1px solid"
        borderColor="neutral.100"
        paddingRight={12}
        paddingLeft={6}
        paddingY={6}
        marginTop={10}
      >
        <ButtonGroup>
          {tabButtonOptions.map((option) => {
            if (!option.enabled) return null;
            return (
              <AssignmentTabButton
                key={option.label}
                label={option.label}
                status={option.status as 'active' | 'inactive'}
                onClick={option.onClick}
              />
            );
          })}
        </ButtonGroup>

        <HStack>
          {Object.keys(recipientSelection).length > 1 && (
            <AssignmentBulkActionsButtonGroup
              payload={assignmentActionPayload}
              refetch={
                type === 'recipients'
                  ? () => queryClient.refetchQueries({ queryKey: ['listAssignmentRecipients'] })
                  : () => queryClient.refetchQueries({ queryKey: ['listWorkflowResultGroups'] })
              }
              rowSelection={recipientSelection}
              setRowSelection={setRecipientSelection}
            />
          )}

          {type !== 'details' && (
            <WorkflowSearch includeCompletionStatusFilter={type === 'recipients'} />
          )}
        </HStack>
      </Flex>

      {type === 'recipients' && <AssignmentDetailsRecipientsView />}

      {type === 'instances' && <AssignmentDetailsInstancesView />}

      {type === 'details' && (
        <AssignmentDetailsView data={isInstanceDetails ? instanceData! : data.workflowData} />
      )}
    </Box>
  );
}
